import {
	Component,
	ContentChild,
	EventEmitter,
	HostBinding,
	Input,
	OnInit,
	Output,
	TemplateRef
}                             from '@angular/core';
import { TranslationService } from '@core/services/translation.service';
import {
	ContentTextTypeEnum,
	ContentTypeEnum
}                             from '@models/common.model';
import {
	ITableCaption,
	ITableCellBase,
	ITableConfig,
	ITableRowItemEvents,
	TableCaptionPositionEnum,
	TableCellTypeEnum,
	TableResponsiveBreakpointEnum,
	TableRowItemEventsEnum
}                             from '@shared/components/table/table.model';
import {
	ContentUtils,
	ObjectUtils
}                             from '@utils/helpers';

@Component( {
	selector: 'app-table[tableConfig]',
	templateUrl: './table.component.html',
	styleUrls: [ './table.component.scss' ]
} )
export class TableComponent implements OnInit {

	private _tableConfig: ITableConfig = {};

	readonly contentTypeEnum = ContentTypeEnum;
	readonly tableResponsiveBreakpointEnum = TableResponsiveBreakpointEnum;
	readonly tableCaptionPositionEnum = TableCaptionPositionEnum;
	readonly tableCellTypeEnum = TableCellTypeEnum;
	readonly contentTextTypeEnum = ContentTextTypeEnum;
	readonly tableRowItemEventsEnum = TableRowItemEventsEnum;

	// not yet implemented
	// readonly tableCellHeaderScopeEnum = TableCellHeaderScopeEnum;


	@Input() set baseClass( baseClass: string ) {
		this.hostClass = this.hostClass = baseClass ? baseClass : null;
	}

	@HostBinding( 'class' ) hostClass: string;
	@HostBinding( 'class.mb-4' ) contentsAreVisible: boolean;

	@Input() set tableConfig( tableConfig: ITableConfig ) {
		this._tableConfig = this.sanitizeTableDataConfig( tableConfig );
	}

	get tableConfig() {
		return this._tableConfig;
	}

	@Input() hoverRowItemByIdentifier: string;

	@Input() registerForRowItemEvents: TableRowItemEventsEnum[] = [];

	@Output() tableRowItemEventEmitter = new EventEmitter<ITableRowItemEvents>();


	@ContentChild( 'tableHeaderItemTpl', { read: TemplateRef } ) tableHeaderItemTpl: TemplateRef<any>;
	@ContentChild( 'tableBodyItemTpl', { read: TemplateRef } ) tableBodyItemTpl: TemplateRef<any>;


	constructor(
		private readonly translationService: TranslationService
	) {}

	private sanitizeTableDataConfig( tableConfig: ITableConfig ): ITableConfig {
		tableConfig = ObjectUtils.deepCopyByRecursion( tableConfig ?? this._tableConfig );

		tableConfig = {
			...tableConfig,
			groupConfig: tableConfig.groupConfig ?? null,
			hoverable: tableConfig?.hoverable ?? true,
			stripped: tableConfig?.stripped ?? true,
			compact: tableConfig?.compact ?? false,
			bordered: tableConfig?.bordered ?? false,
			rowsSpaced: tableConfig?.rowsSpaced ?? false,
			responsive: {
				isActive: tableConfig?.responsive?.isActive ?? false,
				breakpoint: tableConfig?.responsive?.breakpoint ?? TableResponsiveBreakpointEnum.MD
			},
			caption: {
				...tableConfig?.caption,
				contentType: tableConfig?.caption?.contentType ?? this.contentTypeEnum.DEFAULT,
				isHidden: tableConfig?.caption?.isHidden ?? false,
				position: tableConfig?.caption?.position ?? this.tableCaptionPositionEnum.BOTTOM_LEFT,
				isRendered: tableConfig?.caption?.contentType === this.contentTypeEnum.PROJECTED ? true : tableConfig?.caption?.isRendered ?? false
			},
			header: {
				...tableConfig?.header,
				contentType: tableConfig?.header?.contentType ?? this.contentTypeEnum.DEFAULT,
				isHidden: tableConfig?.header?.isHidden ?? false,
				isRendered: tableConfig?.header?.contentType === this.contentTypeEnum.PROJECTED ? true : tableConfig?.header?.isRendered ?? false
			},
			body: {
				...tableConfig?.body,
				contentType: tableConfig?.body?.contentType ?? this.contentTypeEnum.DEFAULT,
				isHidden: tableConfig?.body?.isHidden ?? false,
				isRendered: tableConfig?.body?.contentType === this.contentTypeEnum.PROJECTED ? true : tableConfig?.body?.isRendered ?? true
			},
			footer: {
				...tableConfig?.footer,
				contentType: tableConfig?.footer?.contentType ?? this.contentTypeEnum.DEFAULT,
				isHidden: tableConfig?.footer?.isHidden ?? false,
				isRendered: tableConfig?.footer?.contentType === this.contentTypeEnum.PROJECTED ? true : tableConfig?.footer?.isRendered ?? false
			}
		};

		return tableConfig;
	}


	ngOnInit(): void {}


	isHeaderCellType( cellTypeOverwrite: TableCellTypeEnum, cellItemCellType: TableCellTypeEnum ): boolean {
		if ( cellTypeOverwrite === this.tableCellTypeEnum.HEADER ) { return true; }
		return cellItemCellType === this.tableCellTypeEnum.HEADER;
	}

	getInnerCellContentType( cellItem: ITableCellBase ): string {
		return !!cellItem?.contentType && cellItem?.contentType === this.contentTextTypeEnum.HTML
					 ? 'innerHTML'
					 : 'innerText';
	}

	getValueByCellValueType( cellItem: ITableCellBase ): any {
		return ContentUtils.getValueByValueType(
			cellItem.valueType,
			cellItem.value,
			cellItem.spareValue,
			this.translationService
		);
	}

	getValueByCaptionValueType( captionItem: ITableCaption ): any {
		return ContentUtils.getValueByValueType(
			captionItem.valueType,
			captionItem.value,
			captionItem.spareValue,
			this.translationService
		);
	}

	onGroupContentsVisibilityChange( event ): void {
		const contentsAreHidden: boolean = event;
		const timeout = setTimeout( () => {
			clearTimeout( timeout );
			this.contentsAreVisible = !contentsAreHidden;
		}, 0 );
	}

	isTableCaptionPositionIncluded( tableCaptionPositionList: TableCaptionPositionEnum[] ): boolean {
		return tableCaptionPositionList.includes( this.tableConfig?.caption?.position );
	}

	onRowItemEvent( tableRowItemEvent: ITableRowItemEvents, allowEventEmit: boolean ): void {
		if ( !allowEventEmit ) { return; }

		if (
			this.registerForRowItemEvents?.includes( TableRowItemEventsEnum.ALL ) ||
			this.registerForRowItemEvents?.includes( tableRowItemEvent.event.eventId )
		) {
			this.tableRowItemEventEmitter.emit( tableRowItemEvent );
		}
	}
}
